/**
 * useReviseFlow — submit a revise request to the *current* step's agent.
 *
 * Distinct from `useApproveFlow`: revise targets the CURRENT step's
 * agent (re-runs it with the user's feedback as additional input),
 * while approve targets the NEXT step's agent (advances the workflow).
 * Endpoints differ accordingly:
 *
 *   - Approve  → POST /workflow/resume          (`resumeWorkflow`)
 *   - Revise   → POST /workflow/{tid}/revise    (`reviseWorkflow`)
 *
 * Backend handler at workflow_routes.py:280 → revise_with_feedback()
 * acquires the workflow lock, sets phase=REVISING, and re-runs the
 * target agent with feedback as additional context.
 *
 * Two backend modes (branch on the response's `action_taken`):
 *   - LEGACY (synchronous): the handler blocks through the full agent
 *     re-run (1–4 min) and returns after completion. On success,
 *     schedules a 500ms re-hydrate so the rail dot reflects the new
 *     state quickly; polling then drives status back to `draft`.
 *   - SQS execution mode (`action_taken === 'revision_queued'`,
 *     FEATURE_FLAG_SQS_WORKFLOW_EXECUTION): the handler only ENQUEUES
 *     and returns immediately — the athena-worker picks the message up
 *     seconds later. The hook bridges that gap: holds `revising=true`
 *     (Send disabled, no double-queue) and polls /status until the
 *     target agent is visibly `in_progress`, then refetches and hands
 *     off to the regular 3s polling — from which point behavior is
 *     identical to legacy. Completion-side refresh is already handled
 *     by useRefetchOnReviseComplete (revising→draft transition).
 *
 * Failure path: stores the error on local state, fires an error toast
 * via `notify`, and leaves it up to the consumer (ActionPanel) to
 * surface a chat-bubble error inline.
 */
import { useCallback, useRef, useState } from 'react';
import { getWorkflowStatus, reviseWorkflow } from '../../../api/workflow';
import { stepToAgent } from '../state/workflowV2StepCatalog';
import { notify } from '../../../services/notificationBus';
import { useMountedRef } from '../../../hooks/useMountedRef';

// ── SQS-mode bridge tunables ─────────────────────────────────────────────────
// When the backend runs in SQS execution mode (FEATURE_FLAG_SQS_WORKFLOW_EXECUTION),
// POST /revise returns immediately with `action_taken: "revision_queued"` and the
// re-run happens on the athena-worker pool. Between the enqueue and the worker
// picking the message up, the workflow status still shows the agent as `completed`
// (its pre-revise state) — so the regular 3s polling would flip the rail back to
// `draft`, re-enable Send, and let a fast user queue a SECOND revision. The bridge
// below holds `revising=true` and polls until the backend VISIBLY starts the
// revision (agent `status === 'in_progress'`, which deriveStatus maps to
// `revising`), then hands off to the existing polling exactly as in legacy mode.
const REVISION_START_POLL_MS = 2_500;
// Generous cap on worker pickup (long-poll worst case + cold start), NOT on the
// revision itself — once the re-run is visibly started, the existing status
// polling owns the rest, however long the agent takes.
const REVISION_START_TIMEOUT_MS = 90_000;

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export interface UseReviseFlowInput {
  stepId: number;
  threadId: string | null | undefined;
  refetchStatus: () => void;
}

export interface ReviseFlowState {
  revising: boolean;
  error: string | null;
  onRevise: (feedback: string) => Promise<void>;
  clearError: () => void;
}

export function useReviseFlow({
  stepId,
  threadId,
  refetchStatus,
}: UseReviseFlowInput): ReviseFlowState {
  const [revising, setRevising] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Ref so the closure reads the latest refetch fn without
  // re-creating onRevise on every polling tick.
  const refetchRef = useRef(refetchStatus);
  refetchRef.current = refetchStatus;
  // Story H1 — guard post-await + setTimeout state writes.
  const mountedRef = useMountedRef();

  const onRevise = useCallback(
    async (feedback: string) => {
      const trimmed = (feedback || '').trim();
      if (!trimmed) {
        setError('Feedback is empty.');
        return;
      }
      if (!threadId) {
        setError('Missing thread context.');
        return;
      }
      const target = stepToAgent(stepId);
      if (!target) {
        setError(`No agent for step ${stepId} (revise unavailable).`);
        return;
      }

      setRevising(true);
      setError(null);
      try {
        // The wrapper at api/workflow.js handles the no-/api/v1 prefix
        // via NO_API_V1 internally; we just hand it the inputs.
        const result: any = await reviseWorkflow({
          threadId,
          feedback: trimmed,
          targetAgent: target,
        });

        if (result?.action_taken === 'revision_queued') {
          // SQS execution mode: the backend only ENQUEUED the revision; the
          // worker hasn't started it yet. Hold `revising=true` (Send stays
          // disabled — no double-queue) and poll until the target agent is
          // visibly `in_progress`, then refetch + hand off to the regular
          // polling, which drives revising→draft exactly as in legacy mode.
          const startedAt = Date.now();
          for (;;) {
            await sleep(REVISION_START_POLL_MS);
            if (!mountedRef.current) return; // unmounted mid-bridge: stop silently
            let snapshot: any;
            try {
              snapshot = await getWorkflowStatus(threadId);
            } catch {
              // Transient poll failure — the queue message is durable; keep trying.
              continue;
            }
            const agent = snapshot?.agent_details?.[target];
            if (agent?.status === 'in_progress') {
              // Revision visibly started (deriveStatus → 'revising').
              refetchRef.current?.();
              setRevising(false);
              return;
            }
            if (
              agent?.status === 'failed' ||
              String(snapshot?.workflow_status ?? '').toLowerCase() === 'failed'
            ) {
              throw new Error('Revision failed — the workflow reported a failure.');
            }
            if (Date.now() - startedAt >= REVISION_START_TIMEOUT_MS) {
              throw new Error(
                'Revision was queued but has not started yet — check the workflow status and retry.',
              );
            }
          }
        }

        // Legacy (synchronous) mode: the backend blocked through the full agent
        // re-run, so the work is already done. Keep `revising=true` until the
        // re-hydrate fires so the chat's Send button stays disabled across the
        // brief window between HTTP success and the polling tick flipping
        // status to `revising`. Without this, a fast user could fire a second
        // revise before the backend lock catches up.
        setTimeout(() => {
          if (!mountedRef.current) return;
          refetchRef.current?.();
          setRevising(false);
        }, 500);
      } catch (e: any) {
        const msg =
          e?.response?.data?.detail || e?.message || 'Failed to submit revision';
        if (!mountedRef.current) throw e;
        setError(msg);
        notify({ type: 'error', title: 'Revise failed', message: msg });
        setRevising(false);
        throw e;
      }
    },
    [stepId, threadId],
  );

  const clearError = useCallback(() => setError(null), []);

  return { revising, error, onRevise, clearError };
}
